import { api } from "@/lib/api";
import {
    AdministeringCounty,
    ApnAddressSearchResult,
    AssessorAddressResult,
    CountyConfiguration,
    CountyMailerCreationLog,
    CountyMailerGrantorGranteeIndexFailedRecord,
    CountyMailerGrantorGranteeIndexRecord,
} from "@/types";
import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Checkbox,
    Combobox,
    Divider,
    Group,
    InputBase,
    LoadingOverlay,
    Modal,
    Pagination,
    Paper,
    ScrollArea,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
    useCombobox
} from "@mantine/core";
import { DateInput, DateValue } from "@mantine/dates";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
    IconArrowsSort,
    IconCheck,
    IconEye,
    IconReload,
    IconSortAscendingLetters,
    IconSortDescendingLetters,
    IconX
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../Auth";
import { useHub } from "../Hub";

export type GenerateFailedRecordMailerRequest = {
    countyMailerGrantorGranteeIndexFailedRecordId: number;
    countyCode: string;
    names: string;
    situsAddressOrLegalDescription: string;
    apn: string;
    recordingDate: string;
    generatedDate: string;
    mailingAddress: string;
    mailingCity: string;
    mailingState: string;
    mailingZip: string;
    documentNumber: string;
};

const CountyReports = () => {
    const { currentUser } = useAuth();
    const { hubJobs, removeHubJob } = useHub();
    const [administeringCounties, setAdministeringCounties] = useState<
        AdministeringCounty[]
    >([]);
    const [selectedCounty, setSelectedCounty] =
        useState<AdministeringCounty | null>(null);
    const [countyDetails, setCountyDetails] =
        useState<CountyConfiguration | null>(null);
    const [selectedBatch, setSelectedBatch] =
        useState<CountyMailerCreationLog | null>(null);
    const [generateFailedRecordsModalOpen, generateFailedRecordsModalHandles] =
        useDisclosure(false);
    const [selectedFailedRecord, setSelectedFailedRecord] =
        useState<GenerateFailedRecordMailerRequest | null>(null);
    const [failedRecordError, setFailedRecordError] = useState<string | null>(
        null
    );

    const [apnSearch, setApnSearch] = useDebouncedState<string>("", 300);
    const [apnSearchResults, setApnSearchResults] = useState<
        ApnAddressSearchResult[]
    >([]);
    const lastSearchedApn = useRef<string | null>(null);
    const apnSearchCombobox = useCombobox({
        onDropdownClose: () => apnSearchCombobox.resetSelectedOption(),
    });

    const [visibleBatches, setVisibleBatches] =
        useState<CountyMailerCreationLog[]>();
    const [filteredBatches, setFilteredBatches] =
        useState<CountyMailerCreationLog[]>();
    const [searchBatchesText, setSearchBatchesText] = useState<string>("");
    const [batchesPage, setBatchesPage] = useState<number>(0);
    const [batchesPageSize, setBatchesPageSize] = useState<number>(10);
    const [batchSortTerm, setBatchSortTerm] = useState<string>("date desc");

    const [visibleFailedRecords, setVisibleFailedRecords] =
        useState<CountyMailerGrantorGranteeIndexFailedRecord[]>();
    const [filteredFailedRecords, setFilteredFailedRecords] =
        useState<CountyMailerGrantorGranteeIndexFailedRecord[]>();
    const [searchFailedRecordsText, setSearchFailedRecordsText] =
        useState<string>("");
    const [failedRecordsPage, setFailedRecordsPage] = useState<number>(0);
    const [failedRecordsPageSize, setFailedRecordsPageSize] =
        useState<number>(10);
    const [failedRecordsSortTerm, setFailedRecordsSortTerm] =
        useState<string>("documentNumber asc");

    const [visibleProcessedRecords, setVisibleProcessedRecords] =
        useState<CountyMailerGrantorGranteeIndexRecord[]>();
    const [filteredProcessedRecords, setFilteredProcessedRecords] =
        useState<CountyMailerGrantorGranteeIndexRecord[]>();
    const [searchProcessedRecordsText, setSearchProcessedRecordsText] =
        useState<string>("");
    const [processedRecordsPage, setProcessedRecordsPage] = useState<number>(0);
    const [processedRecordsPageSize, setProcessedRecordsPageSize] =
        useState<number>(10);
    const [processedRecordsSortTerm, setProcessedRecordsSortTerm] =
        useState<string>("documentNumber asc");

    // const [deliveryMethod, setDeliveryMethod] = useState<"Email" | "FTP" | "None">("None");
    // const [ftpProtocol, setFtpProtocol] = useState<"ftp" | "sftp">("ftp");
    // const [emailAddress, setEmailAddress] = useState<string>("");
    // const [ftpHost, setFtpHost] = useState<string>("");
    // const [ftpPort, setFtpPort] = useState<string | number>(21);
    // const [ftpUseSsl, setFtpUseSsl] = useState<boolean>(false);
    // const [ftpUsername, setFtpUsername] = useState<string>("");
    // const [ftpPassword, setFtpPassword] = useState<string>("");
    // const [ftpDirectory, setFtpDirectory] = useState<string>("");
    // const [ftpPassiveMode, setFtpPassiveMode] = useState<boolean>(true);
    // const [deliveryMethodModalOpen, deliveryMethodModalHandles] =
    //     useDisclosure(false);

    const [batchDownloadInProgress, setBatchDownloadInProgress] = useState<boolean>(false);
    const [batchRecordUpdateInProgress, setBatchRecordUpdateInProgress] = useState<boolean>(false);
    const [batchFailedRecordUpdateInProgress, setBatchFailedRecordUpdateInProgress] = useState<boolean>(false);

    useEffect(() => {
        for (const jobId in hubJobs) {
            const job = hubJobs[jobId];
            notifications.hide(jobId);
            if (job.status === "Completed") {
                notifications.show({
                    id: jobId,
                    title: "Job Completed",
                    message: job.message || "The job has completed successfully.",
                    color: "green",
                    "aria-live": "polite",
                    closeButtonProps:{
                        "aria-label": "Close success notification"
                    }
                });
                if (selectedBatch?.jobId == jobId) {
                    selectedBatch.regenerateMailersInProgress = false;
                    selectedBatch.jobId = undefined;
                }
                selectedBatch?.grantorGranteeIndexRecords.forEach((r) => {
                    if (r.jobId === jobId) {
                        r.generatingInProcess = false;
                        r.jobId = undefined;
                    }
                });
                selectedBatch?.grantorGranteeIndexFailedRecords.forEach((r) => {
                    if (r.jobId === jobId) {
                        r.generatingInProcess = false;
                        r.jobId = undefined;
                    }
                });
                setSelectedBatch({ ...selectedBatch! });
                if (job.resultUrl) {
                    downloadFile(job.resultUrl);
                }
                removeHubJob(jobId);
            }
            else if (job.status === "Failed") {
                notifications.show({
                    id: jobId,
                    title: "Job Failed",
                    message: job.message || "The job has failed.",
                    color: "red",
                    "aria-live": "polite",
                    closeButtonProps:{
                        "aria-label": "Close error notification"
                    }
                });
                if (selectedBatch?.jobId == jobId) {
                    selectedBatch.regenerateMailersInProgress = false;
                    selectedBatch.jobId = undefined;
                }
                selectedBatch?.grantorGranteeIndexRecords.forEach((r) => {
                    if (r.jobId === jobId) {
                        r.generatingInProcess = false;
                        r.jobId = undefined;
                    }
                });
                selectedBatch?.grantorGranteeIndexFailedRecords.forEach((r) => {
                    if (r.jobId === jobId) {
                        r.generatingInProcess = false;
                        r.jobId = undefined;
                    }
                });
                setSelectedBatch({ ...selectedBatch! });
                removeHubJob(jobId);
            }
            else if (job.status === "InProgress") {
                notifications.show({
                    id: jobId,
                    title: "Job In Progress",
                    message: job.message || "The job is currently in progress.",
                    color: "blue",
                    "aria-live": "polite",
                    closeButtonProps:{
                        "aria-label": "Close info notification"
                    }
                });
            }
            else {
                notifications.show({
                    id: jobId,
                    title: "Job Pending",
                    message: job.message || "The job is pending.",
                    color: "yellow",
                    "aria-live": "polite",
                    closeButtonProps:{
                        "aria-label": "Close warning notification"
                    }
                });
            }

        }
    }, [hubJobs]);

    useEffect(() => {
        let processedRecords = [
            ...(selectedBatch?.grantorGranteeIndexRecords || []),
        ];
        if (searchProcessedRecordsText) {
            processedRecords = processedRecords.filter(
                (record) =>
                    record.documentNumber.includes(searchProcessedRecordsText) ||
                    record.documentTitle.includes(searchProcessedRecordsText) ||
                    record.apn.includes(searchProcessedRecordsText)
            );
        }
        setFilteredProcessedRecords([...processedRecords]);
        setVisibleProcessedRecords([
            ...processedRecords
                .sort((a, b) => {
                    if (processedRecordsSortTerm.includes("documentNumber")) {
                        return processedRecordsSortTerm.endsWith("asc")
                            ? a.documentNumber.localeCompare(b.documentNumber)
                            : b.documentNumber.localeCompare(a.documentNumber);
                    } else if (processedRecordsSortTerm.includes("dateCreated")) {
                        return processedRecordsSortTerm.endsWith("asc")
                            ? a.createdAt.localeCompare(b.createdAt)
                            : b.createdAt.localeCompare(a.createdAt);
                    } else if (processedRecordsSortTerm.includes("apn")) {
                        return processedRecordsSortTerm.endsWith("asc")
                            ? a.apn.localeCompare(b.apn)
                            : b.apn.localeCompare(a.apn);
                    } else if (processedRecordsSortTerm.includes("documentDate")) {
                        return processedRecordsSortTerm.endsWith("asc")
                            ? a.documentDate.localeCompare(b.documentDate)
                            : b.documentDate.localeCompare(a.documentDate);
                    } else if (processedRecordsSortTerm.includes("id")) {
                        return processedRecordsSortTerm.endsWith("asc")
                            ? a.id > b.id
                                ? 1
                                : -1
                            : b.id > a.id
                                ? 1
                                : -1;
                    }
                    return 0;
                })
                .slice(
                    processedRecordsPage * processedRecordsPageSize,
                    (processedRecordsPage + 1) * processedRecordsPageSize
                ),
        ]);
    }, [
        selectedBatch,
        searchProcessedRecordsText,
        processedRecordsPage,
        processedRecordsPageSize,
        processedRecordsSortTerm,
    ]);

    useEffect(() => {
        let failedRecords = [
            ...(selectedBatch?.grantorGranteeIndexFailedRecords || []),
        ];
        if (searchFailedRecordsText) {
            failedRecords = failedRecords.filter(
                (record) =>
                    record.documentNumber.includes(searchFailedRecordsText) ||
                    record.documentTitle.includes(searchFailedRecordsText) ||
                    record.apn.includes(searchFailedRecordsText)
            );
        }
        setFilteredFailedRecords([...failedRecords]);
        setVisibleFailedRecords([
            ...failedRecords
                .sort((a, b) => {
                    if (failedRecordsSortTerm.includes("documentNumber")) {
                        return failedRecordsSortTerm.endsWith("asc")
                            ? a.documentNumber.localeCompare(b.documentNumber)
                            : b.documentNumber.localeCompare(a.documentNumber);
                    } else if (failedRecordsSortTerm.includes("dateCreated")) {
                        return failedRecordsSortTerm.endsWith("asc")
                            ? a.createdAt.localeCompare(b.createdAt)
                            : b.createdAt.localeCompare(a.createdAt);
                    } else if (failedRecordsSortTerm.includes("apn")) {
                        return failedRecordsSortTerm.endsWith("asc")
                            ? a.apn.localeCompare(b.apn)
                            : b.apn.localeCompare(a.apn);
                    } else if (failedRecordsSortTerm.includes("documentDate")) {
                        return failedRecordsSortTerm.endsWith("asc")
                            ? a.documentDate.localeCompare(b.documentDate)
                            : b.documentDate.localeCompare(a.documentDate);
                    } else if (failedRecordsSortTerm.includes("documentTitle")) {
                        return failedRecordsSortTerm.endsWith("asc")
                            ? a.documentTitle.localeCompare(b.documentTitle)
                            : b.documentTitle.localeCompare(a.documentTitle);
                    } else if (failedRecordsSortTerm.includes("id")) {
                        return failedRecordsSortTerm.endsWith("asc")
                            ? a.id > b.id
                                ? 1
                                : -1
                            : b.id > a.id
                                ? 1
                                : -1;
                    }
                    return 0;
                })
                .slice(
                    failedRecordsPage * failedRecordsPageSize,
                    (failedRecordsPage + 1) * failedRecordsPageSize
                ),
        ]);
    }, [
        selectedBatch,
        searchFailedRecordsText,
        failedRecordsPage,
        failedRecordsPageSize,
        failedRecordsSortTerm,
    ]);

    useEffect(() => {
        // setDeliveryMethod(countyDetails?.deliveryMethod  === "email" ? "Email" : countyDetails?.deliveryMethod === "ftp" ? "FTP" : "None");
        // setEmailAddress(countyDetails?.emailConfiguration?.emailAddress || "");
        // setFtpProtocol(countyDetails?.ftpConfiguration?.protocol || "ftp");
        // setFtpHost(countyDetails?.ftpConfiguration?.host || "");
        // setFtpPort(countyDetails?.ftpConfiguration?.port || 21);
        // setFtpUseSsl(countyDetails?.ftpConfiguration?.useSSL || false);
        // setFtpUsername(countyDetails?.ftpConfiguration?.username || "");
        // setFtpPassword(countyDetails?.ftpConfiguration?.password || "");
        // setFtpDirectory(countyDetails?.ftpConfiguration?.remotePath || "");
        let batches = [...(countyDetails?.countyMailerCreationLogs || [])];
        if (searchBatchesText) {
            batches = batches.filter((batch) =>
                batch.id.toString().includes(searchBatchesText)
            );
        }
        setFilteredBatches([...batches]);
        setVisibleBatches([
            ...batches
                .sort((a, b) => {
                    if (batchSortTerm === "date asc") {
                        return a.createdAt.localeCompare(b.createdAt);
                    } else {
                        return b.createdAt.localeCompare(a.createdAt);
                    }
                })
                .slice(
                    batchesPage * batchesPageSize,
                    (batchesPage + 1) * batchesPageSize
                ),
        ]);
    }, [
        countyDetails,
        searchBatchesText,
        batchesPage,
        batchesPageSize,
        batchSortTerm,
    ]);

    useEffect(() => {
        if (currentUser && selectedCounty === null) {
            setAdministeringCounties(currentUser.administeringCounties || []);
            setSelectedCounty(
                currentUser.administeringCounties &&
                    currentUser.administeringCounties.length > 0
                    ? currentUser.administeringCounties[0]
                    : null
            );
        }
    }, [currentUser]);

    useEffect(() => {
        if (selectedCounty) {
            fetchCounty();
        }
    }, [selectedCounty]);

    const downloadFile = async (url: string) => {
        const result: any = await api.get(url, { responseType: 'blob' });
        const fileName = url.split('fileName=').pop();
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(result);
        downloadLink.download = fileName || 'downloaded_file';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }
    const formatDate = (dateString: string) => {
        const jsDate = new Date(dateString);
        const year = jsDate.toLocaleString("default", { year: "numeric" });
        const month = jsDate.toLocaleString("default", { month: "long" });
        const day = jsDate.toLocaleString("default", { day: "numeric" });
        return `${month} ${day}, ${year}`;
    };
    const formatMailerDate = (dateString: string) => {
        const jsDate = new Date(dateString);
        const year = jsDate.toLocaleString("default", { year: "numeric" });
        const month = jsDate.toLocaleString("default", { month: "numeric" });
        const day = jsDate.toLocaleString("default", { day: "numeric" });
        return `${month}/${day}/${year}`;
    };
    const stripLeadingZeros = (value?: string) =>
        (value ?? "").replace(/\d+/g, (group) => group.replace(/^0+(?=\d)/, ""));
    const formatRange = (min?: string, max?: string) => {
        const low = stripLeadingZeros(min);
        const high = stripLeadingZeros(max);
        if (!low && !high) return "N/A";
        return `${low} - ${high}`;
    };
    const fetchCounty = async () => {
        {
            const selectedCountyCode = selectedCounty?.countyCode;
            if (selectedCountyCode) {
                try {
                    const response: CountyConfiguration = await api.get(
                        `/admin/counties/${selectedCountyCode}`
                    );
                    setCountyDetails(response);
                } catch (error) {
                    console.error("Failed to fetch county details:", error);
                    setSelectedCounty(null);
                    setSelectedBatch(null);
                }
            }
        }
    };
    const handleViewBatch = async (batchId: number) => {
        try{
            const response: CountyMailerCreationLog = await api.get(`/admin/batch/${batchId}`);
            setSelectedBatch(response);
        }
        catch (error) {
            console.error("Failed to fetch batch details:", error);
            notifications.show({
                title: "Error",
                message: "Failed to fetch batch details. Please try again.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
    };

    const viewCountyDocument = async (record: CountyMailerGrantorGranteeIndexRecord | CountyMailerGrantorGranteeIndexFailedRecord) => {
        try {
            const result: any = await api.get(`/admin/download-county-document?countyCode=${selectedCounty?.countyCode}&documentNumber=${record.documentNumber}&year=${record.documentDate.substring(6)}`, { responseType: 'blob' });
            const fileName = `Document_${record.documentNumber}.pdf`;
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(result);
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } catch (error) {
            console.error("Failed to view county document:", error);
            notifications.show({
                title: "Error",
                message: "Failed to view county document. Please try again.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
    }

    const viewBatchDocument = async (record: CountyMailerCreationLog) => {
        try {
            setBatchDownloadInProgress(true);
            const result: any = await api.get(`document/download?containerName=countymailers&fileName=${record.batchId}.pdf`, {
                responseType: 'blob'})
            const fileName = `${record.batchId}.pdf`;
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(result);
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            
        } catch (error) {
            console.error("Error fetching document:", error);
            
        }
        finally {
            setBatchDownloadInProgress(false);
        }
    }

    // const handleRegenerateMailersForBatch = async () => {
    //     try {
    //         selectedBatch!.regenerateMailersInProgress = true;
    //         setSelectedBatch({ ...selectedBatch! });
    //         const result: any = await api.post(`/admin/batch/${selectedBatch!.id}/rerun`);
    //         selectedBatch!.jobId = result.jobId;
    //         setSelectedBatch({ ...selectedBatch! });
    //     }
    //     catch (error) {
    //         console.error("Failed to regenerate mailers for batch:", error);
    //         selectedBatch!.regenerateMailersInProgress = false;
    //         setSelectedBatch({ ...selectedBatch! });

    //     }
    // };
    // const handleRecreateMailer = async (
    //     record: CountyMailerGrantorGranteeIndexRecord
    // ) => {
    //     try {
    //         selectedBatch?.grantorGranteeIndexRecords.forEach((r) => {
    //             if (r.id === record.id) {
    //                 r.generatingInProcess = true;
    //             }
    //         });
    //         setSelectedBatch({ ...selectedBatch! });
    //         const result: any = await api.post(`/admin/regenerate-mailer`, record, {
    //             responseType: "json",
    //         });
    //         selectedBatch?.grantorGranteeIndexRecords.forEach((r) => {
    //             if (r.id === record.id) {
    //                 r.jobId = result.jobId;
    //             }
    //         });
    //         setSelectedBatch({ ...selectedBatch! });
    //     } catch (error) {
    //         console.error("Failed to recreate mailer:", error);
    //         selectedBatch?.grantorGranteeIndexRecords.forEach((r) => {
    //             if (r.id === record.id) {
    //                 r.generatingInProcess = false;
    //             }
    //         });
    //         setSelectedBatch({ ...selectedBatch! });
    //     }
    // };

    //Need to allow for spaces in raw input and leave them in the formatted version if the mask includes them
    //Mask definitions: 0 = number, a = letter, - = dash, c=number, letter, or space all other characters are treated as literals and included in the masked value
    const maskValue = (value: string, mask: string): string => {
        const raw = mask.includes('s') ? value.replace(/[^a-zA-Z0-9\s]/g, '') : value.replace(/[^a-zA-Z0-9]/g, '');
        let maskedValue = '';
        let rawIndex = 0;
        for (let i = 0; i < mask.length && rawIndex < raw.length; i++) {
            const patternChar = mask[i];
            const currentRawChar = raw[rawIndex];
            if (patternChar === '0' && /\d/.test(currentRawChar)) {
                maskedValue += currentRawChar;
                rawIndex++;
            }
            else if (patternChar === 'a' && /[a-zA-Z]/.test(currentRawChar)) {
                maskedValue += currentRawChar.toUpperCase();
                rawIndex++;
            } else if (patternChar === '-') { // dash
                maskedValue += '-';
            } else if (patternChar === 'c') { // any character
                maskedValue += currentRawChar.toUpperCase();
                rawIndex++;
            } else {
                maskedValue += patternChar;
            }
        }
        return maskedValue;
    };

    const searchApns = useCallback(
        async (searchTerm: string) => {
            lastSearchedApn.current = searchTerm;
            if (!selectedCounty || searchTerm.trim().length === 0) {
                setApnSearchResults([]);
                return;
            }
            try {
                const results: ApnAddressSearchResult[] = await api.get(
                    `/search/apn?SearchTerm=${searchTerm}&County=${selectedCounty.countyName}`
                );
                setApnSearchResults(results);
            } catch (error) {
                console.error(error);
                lastSearchedApn.current = null;
                setApnSearchResults([]);
            }
        },
        [selectedCounty]
    );

    useEffect(() => {
        if (apnSearch === lastSearchedApn.current) {
            return;
        }
        searchApns(apnSearch);
    }, [apnSearch, searchApns]);

    const handleApnSelected = async (apn: string) => {
        apnSearchCombobox.closeDropdown();
        setApnSearch(apn);
        lastSearchedApn.current = apn;
        if (!selectedFailedRecord) {
            return;
        }
        try {
            const address: AssessorAddressResult = await api.get(
                `/admin/assessor-address?countyCode=${selectedFailedRecord.countyCode}&apn=${apn}`
            );
            setSelectedFailedRecord({
                ...selectedFailedRecord,
                apn,
                names: address.names,
                situsAddressOrLegalDescription:
                    address.situsAddressOrLegalDescription,
                mailingAddress: address.mailingAddress,
                mailingCity: address.mailingCity,
                mailingState: address.mailingState,
                mailingZip: address.mailingZip,
            });
        } catch (error) {
            console.error(error);
            setSelectedFailedRecord({ ...selectedFailedRecord, apn });
            notifications.show({
                title: "Error",
                message: "Failed to look up the address for that APN.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps: { "aria-label": "Close error notification" },
            });
        }
    };

    const handleGenerateFailedRecordMailer = (
        record: CountyMailerGrantorGranteeIndexFailedRecord
    ) => {
        setFailedRecordError(null);
        setApnSearchResults([]);
        lastSearchedApn.current = null;
        setSelectedFailedRecord({
            countyMailerGrantorGranteeIndexFailedRecordId: record.id,
            countyCode: selectedCounty ? selectedCounty.countyCode : "",
            documentNumber: record.documentNumber,
            names: record.names || "",
            situsAddressOrLegalDescription: record.situsAddressOrLegalDescription || "",
            apn: record.apn,
            recordingDate: record.documentDate,
            generatedDate: formatDate(new Date().toISOString()),
            mailingAddress: record.mailingAddress || "",
            mailingCity: record.mailingCity || "",
            mailingState: record.mailingState || "",
            mailingZip: record.mailingZip || "",
        });
        generateFailedRecordsModalHandles.open();
    };

    const handleGenerateFailedRecordMailerSubmit = async () => {
        setFailedRecordError(null);
        if (selectedFailedRecord) {
            const {
                names,
                situsAddressOrLegalDescription,
                apn,
                mailingAddress,
                mailingCity,
                mailingState,
                mailingZip,
            } = selectedFailedRecord;
            if (
                !names ||
                !situsAddressOrLegalDescription ||
                !mailingAddress ||
                !mailingCity ||
                !mailingState ||
                !mailingZip ||
                !apn
            ) {
                setFailedRecordError("Please fill in all required fields.");
                return;
            }

            try {
                selectedBatch?.grantorGranteeIndexFailedRecords.forEach((r) => {
                    if (r.id === selectedFailedRecord.countyMailerGrantorGranteeIndexFailedRecordId) {
                        r.generatingInProcess = true;
                    }
                });
                setSelectedBatch({ ...selectedBatch! });
                const result: any = await api.post(
                    `/admin/regenerate-failed-mailer`,
                    selectedFailedRecord);
                selectedBatch?.grantorGranteeIndexFailedRecords.forEach((r) => {
                    if (r.id === selectedFailedRecord.countyMailerGrantorGranteeIndexFailedRecordId) {
                        r.jobId = result.jobId;
                    }
                });
                await handleViewBatch(selectedBatch!.id);
                generateFailedRecordsModalHandles.close();
            } catch (error) {
                console.error("Error generating mailer:", error);
            }
        }
    };

    // const handleSetCountyDeliveryMethod = async (newDeliveryMethod: string) => {
    //     try {
    //         if (!countyDetails) {
    //             return;
    //         }
    //         switch (newDeliveryMethod) {
    //             case "Email":
    //                 //Validate Email field
    //                 if (!emailAddress) {
    //                     notifications.show({
    //                         title: "Error",
    //                         message: "Please provide a valid email address.",
    //                         color: "red",
    //                         position: "top-center",
    //                         "aria-live": "polite",
    //                         closeButtonProps:{
    //                             "aria-label": "Close error notification"
    //                         }
    //                     })
    //                     return;
    //                 }
    //                 const result: CountyConfiguration = await api.put(`/admin/counties/${countyDetails.id}/delivery/email`, {
    //                     emailAddress,
    //                     countyId: countyDetails.id,
    //                 });
    //                 setDeliveryMethod("Email");
    //                 setCountyDetails(result);
    //                 notifications.show({
    //                     title: "Success",
    //                     message: "Email delivery method set successfully.",
    //                     color: "green",
    //                     position: "top-center",
    //                     "aria-live": "polite",
    //                     closeButtonProps:{
    //                         "aria-label": "Close success notification"
    //                     }
    //                 });
    //                 deliveryMethodModalHandles.close();
    //                 break;
    //             case "FTP":
    //                 //Validate FTP fields
    //                 if (!ftpHost || !ftpPort || !ftpUsername || !ftpPassword) {
    //                     notifications.show({
    //                         title: "Error",
    //                         message: "Please fill in all required FTP fields. (Host, Port, Username, Password)",
    //                         color: "red",
    //                         position: "top-center",
    //                         "aria-live": "polite",
    //                         closeButtonProps:{
    //                             "aria-label": "Close error notification"
    //                         }
    //                     })
    //                     return;
    //                 }
    //                 const ftpResult: CountyConfiguration = await api.put(`/admin/counties/${countyDetails.id}/delivery/ftp`, {
    //                     protocol: ftpProtocol,
    //                     host: ftpHost,
    //                     port: Number(ftpPort),
    //                     useSsl: ftpUseSsl,
    //                     usePassive: ftpPassiveMode,
    //                     username: ftpUsername,
    //                     password: ftpPassword,
    //                     remotePath: ftpDirectory,
    //                     countyId: countyDetails.id,
    //                 });
    //                 setDeliveryMethod("FTP");
    //                 setCountyDetails(ftpResult);
    //                 notifications.show({
    //                     title: "Success",
    //                     message: "FTP delivery method set successfully.",
    //                     color: "green",
    //                     position: "top-center",
    //                     "aria-live": "polite",
    //                     closeButtonProps:{
    //                         "aria-label": "Close success notification"
    //                     }
    //                 });
    //                 deliveryMethodModalHandles.close();
    //                 break;
    //         }
    //     }
    //     catch (error) {
    //         console.error("Error setting delivery method:", error);
    //         notifications.show({
    //             title: "Error",
    //             message: "Failed to set delivery method",
    //             color: "red",
    //             position: "top-center",
    //             "aria-live": "polite",
    //             closeButtonProps:{
    //                 "aria-label": "Close error notification"
    //             }
    //         });
    //     }
    // }

    const handleMarkBatchAsMailed = async (batchId: number) => {
        try {
            setBatchRecordUpdateInProgress(true);
            await api.post(`/admin/batch/${batchId}/mailed`);
            notifications.show({
                title: "Success",
                message: "Batch marked as mailed successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error marking batch as mailed:", error);
            notifications.show({
                title: "Error",
                message: "Failed to mark batch as mailed.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchRecordUpdateInProgress(false);
        }
    };

    // const handleMarkRecordAsMailed = async (recordId: number, batchId: number) => {
    //     try {
    //         await api.post(`/admin/record/${recordId}/mailed`);
    //         notifications.show({
    //             title: "Success",
    //             message: "Record marked as mailed successfully.",
    //             color: "green",
    //             position: "top-center",
    //             "aria-live": "polite",
    //             closeButtonProps:{
    //                 "aria-label": "Close success notification"
    //             }
    //         });
    //         await handleViewBatch(batchId);
    //     } catch (error) {
    //         console.error("Error marking record as mailed:", error);
    //         notifications.show({
    //             title: "Error",
    //             message: "Failed to mark record as mailed.",
    //             color: "red",
    //             position: "top-center",
    //             "aria-live": "polite",
    //             closeButtonProps:{
    //                 "aria-label": "Close error notification"
    //             }
    //         });
    //     }
    // };

    const handleToggleRecordMailedStatus = async (recordId: number, batchId: number) => {
        try {
            setBatchRecordUpdateInProgress(true);
            await api.post(`/admin/record/${recordId}/mailed/toggle`);
            notifications.show({
                title: "Success",
                message: "Record mailed status toggled successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error toggling record mailed status:", error);
            notifications.show({
                title: "Error",
                message: "Failed to toggle record mailed status.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchRecordUpdateInProgress(false);
        }
    };

    const handleUpdateRecordMailedDate = async (recordId: number, batchId: number, mailedAt: string) => {
        try {
            setBatchRecordUpdateInProgress(true);
            await api.post(`/admin/record/${recordId}/mailed/update`, { mailedAt });
            notifications.show({
                title: "Success",
                message: "Record mailed date updated successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error updating record mailed date:", error);
            notifications.show({
                title: "Error",
                message: "Failed to update record mailed date.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchRecordUpdateInProgress(false);
        }
    };

    const handleClearRecordMailedDate = async (recordId: number, batchId: number) => {
        try {
            await api.post(`/admin/record/${recordId}/mailed/clear`);
            notifications.show({
                title: "Success",
                message: "Record mailed date cleared successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error clearing record mailed date:", error);
            notifications.show({
                title: "Error",
                message: "Failed to clear record mailed date.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
    };

    const handleMarkFailedRecordsBatchAsMailed = async (batchId: number) => {
        try {
            setBatchFailedRecordUpdateInProgress(true);
            await api.post(`/admin/batch/${batchId}/failed/mailed`);
            notifications.show({
                title: "Success",
                message: "Failed records batch marked as mailed successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error marking failed records batch as mailed:", error);
            notifications.show({
                title: "Error",
                message: "Failed to mark failed records batch as mailed.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchFailedRecordUpdateInProgress(false);
        }
    };

    // const handleMarkFailedRecordAsMailed = async (recordId: number, batchId: number) => {
    //     try {
    //         await api.post(`/admin/record/${recordId}/failed/mailed`);
    //         notifications.show({
    //             title: "Success",
    //             message: "Failed record marked as mailed successfully.",
    //             color: "green",
    //             position: "top-center",
    //             "aria-live": "polite",
    //             closeButtonProps:{
    //                 "aria-label": "Close success notification"
    //             }
    //         });
    //         await handleViewBatch(batchId);
    //     } catch (error) {
    //         console.error("Error marking failed record as mailed:", error);
    //         notifications.show({
    //             title: "Error",
    //             message: "Failed to mark failed record as mailed.",
    //             color: "red",
    //             position: "top-center",
    //             "aria-live": "polite",
    //             closeButtonProps:{
    //                 "aria-label": "Close error notification"
    //             }
    //         });
    //     }
    // };

    const handleToggleFailedRecordMailedStatus = async (recordId: number, batchId: number) => {
        try {
            setBatchFailedRecordUpdateInProgress(true);
            await api.post(`/admin/record/${recordId}/failed/mailed/toggle`);
            notifications.show({
                title: "Success",
                message: "Failed record mailed status toggled successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error toggling failed record mailed status:", error);
            notifications.show({
                title: "Error",
                message: "Failed to toggle failed record mailed status.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchFailedRecordUpdateInProgress(false);
        }
    };

    const handleUpdateFailedRecordMailedDate = async (recordId: number, batchId: number, mailedAt: string) => {
        try {
            setBatchFailedRecordUpdateInProgress(true);
            await api.post(`/admin/record/${recordId}/failed/mailed/update`, { mailedAt });
            notifications.show({
                title: "Success",
                message: "Failed record mailed date updated successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error updating failed record mailed date:", error);
            notifications.show({
                title: "Error",
                message: "Failed to update failed record mailed date.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchFailedRecordUpdateInProgress(false);
        }
    };

    const handleClearFailedRecordMailedDate = async (recordId: number, batchId: number) => {
        try {
            setBatchFailedRecordUpdateInProgress(true);
            await api.post(`/admin/record/${recordId}/failed/mailed/clear`);
            notifications.show({
                title: "Success",
                message: "Failed record mailed date cleared successfully.",
                color: "green",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close success notification"
                }
            });
            await handleViewBatch(batchId);
        } catch (error) {
            console.error("Error clearing failed record mailed date:", error);
            notifications.show({
                title: "Error",
                message: "Failed to clear failed record mailed date.",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        }
        finally {
            setBatchFailedRecordUpdateInProgress(false);
        }
    };

    const resetRecordsViewStates = () => {
        setSelectedBatch(null);
        setSelectedFailedRecord(null);
        setFailedRecordError(null);
        setSearchBatchesText("");
        setBatchesPage(0);
        setBatchesPageSize(10);
        setBatchSortTerm("date desc");
        setVisibleProcessedRecords(undefined);
        setFilteredProcessedRecords(undefined);
        setSearchProcessedRecordsText("");
        setProcessedRecordsPage(0);
        setProcessedRecordsPageSize(10);
        setProcessedRecordsSortTerm("documentNumber asc");
        setVisibleFailedRecords(undefined);
        setFilteredFailedRecords(undefined);
        setSearchFailedRecordsText("");
        setFailedRecordsPage(0);
        setFailedRecordsPageSize(10);
        setFailedRecordsSortTerm("documentNumber asc");
    };

    return (
        <>
            <Stack>
                <Group mb="md" align="center" justify="space-between">
                    <h2>{`${selectedCounty ? selectedCounty.countyName : "N/A"
                        } Printed Notifications: Stats and Management`}</h2>
                    {administeringCounties.length > 1 && (
                        <Select
                            placeholder="Select County"
                            data={administeringCounties.map((county) => ({
                                value: county.countyCode,
                                label: county.countyName,
                            }))}
                            value={selectedCounty ? selectedCounty.countyCode : null}
                            onChange={(value) => {
                                const county = administeringCounties.find(
                                    (c) => c.countyCode === value
                                );
                                setSearchBatchesText("");
                                setBatchesPage(0);
                                setBatchesPageSize(10);
                                setBatchSortTerm("date desc");
                                setSelectedCounty(county || null);
                            }}
                        />
                    )}
                </Group>
               <Paper withBorder p="md" m="md" radius={"md"}>
                            {selectedBatch === null &&
                                (countyDetails?.countyMailerCreationLogs.length ?? 0) == 0 && (
                                    <Alert color="green">No Batches Found</Alert>
                                )}
                            {selectedBatch === null &&
                                (countyDetails?.countyMailerCreationLogs.length ?? 0) > 0 && (
                                    <>
                                        <Title order={3} mb="md">{`Batches`}</Title>
                                        <Group justify="flex-end" mt="md">
                                            <TextInput
                                                placeholder="Search..."
                                                value={searchBatchesText}
                                                onChange={(event) =>
                                                    setSearchBatchesText(event.currentTarget.value)
                                                }
                                            />
                                            <Select
                                                value={batchesPageSize.toString()}
                                                onChange={(value) => setBatchesPageSize(Number(value))}
                                                data={[
                                                    { value: "10", label: "10" },
                                                    { value: "20", label: "20" },
                                                    { value: "50", label: "50" },
                                                    { value: "100", label: "100" },
                                                ]}
                                            />
                                            <Pagination
                                                total={Math.ceil(
                                                    (filteredBatches?.length ?? 0) / batchesPageSize
                                                )}
                                                value={batchesPage + 1}
                                                onChange={(value) => {
                                                    setBatchesPage(value - 1);
                                                }}
                                            />
                                        </Group>
                                        <Table striped highlightOnHover>
                                            <Table.Thead>
                                                <Table.Tr style={{ whiteSpace: "nowrap" }}>
                                                    <Table.Th>
                                                        <Group
                                                            tabIndex={0}
                                                            // role="button"
                                                            // onClick={() => {
                                                            //     const newSortTerm = batchSortTerm.includes("id")
                                                            //         ? batchSortTerm.includes("asc")
                                                            //             ? "id desc"
                                                            //             : "id asc"
                                                            //         : "id asc";
                                                            //     setBatchSortTerm(newSortTerm);
                                                            // }}
                                                        >
                                                            <Text fz={13}>Batch Number</Text>
                                                            {/* {batchSortTerm.includes("id") ? (
                                                                batchSortTerm.includes("desc") ? (
                                                                    <IconSortDescendingLetters aria-hidden="true" />
                                                                ) : (
                                                                    <IconSortAscendingLetters aria-hidden="true" />
                                                                )
                                                            ) : (
                                                                <IconArrowsSort aria-hidden="true" />
                                                            )} */}
                                                        </Group>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Group
                                                            wrap="nowrap"
                                                            tabIndex={0}
                                                            role="button"
                                                            onClick={() => {
                                                                const newSortTerm = batchSortTerm.includes(
                                                                    "createdAt"
                                                                )
                                                                    ? batchSortTerm.includes("asc")
                                                                        ? "createdAt desc"
                                                                        : "createdAt asc"
                                                                    : "createdAt asc";
                                                                setBatchSortTerm(newSortTerm);
                                                            }}
                                                        >
                                                            <Text fz={13}>Batch Date</Text>
                                                            {batchSortTerm.includes("createdAt") ? (
                                                                batchSortTerm.includes("desc") ? (
                                                                    <IconSortDescendingLetters aria-hidden="true" />
                                                                ) : (
                                                                    <IconSortAscendingLetters aria-hidden="true" />
                                                                )
                                                            ) : (
                                                                <IconArrowsSort aria-hidden="true" />
                                                            )}
                                                        </Group>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Recording Date Range</Text>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Document # Range</Text>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Total Records</Text>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Letters Required</Text>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Letters Generated</Text>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Letters Failed</Text>
                                                    </Table.Th>
                                                    <Table.Th>
                                                        <Text fz={13}>Actions</Text>
                                                    </Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>

                                            <Table.Tbody>
                                                {visibleBatches?.map((batch) => (
                                                    <Table.Tr key={batch.id}>
                                                        <Table.Td>{batch.batchId}</Table.Td>
                                                        {/* <Table.Td>TUO-78.2</Table.Td> */}
                                                        <Table.Td>
                                                            {formatMailerDate(batch.createdAt)}
                                                        </Table.Td>
                                                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                                                            {formatRange(batch.minDate, batch.maxDate)}
                                                        </Table.Td>
                                                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                                                            {formatRange(batch.minDocumentNumber, batch.maxDocumentNumber)}
                                                        </Table.Td>
                                                        <Table.Td>{batch.totalRecords}</Table.Td>
                                                        <Table.Td>
                                                            {batch.totalRecordsMatchingDocumentTypes}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {batch.totalRecordsSuccessfullyCreated}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {batch.totalRecordsFailed}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Group>
                                                                <Tooltip
                                                                    label={`View ${batch.batchId} details`}
                                                                    withArrow
                                                                >
                                                                    <ActionIcon
                                                                        variant="outline"
                                                                        color="pq-red"
                                                                        aria-label={`View ${batch.batchId} details`}
                                                                        onClick={async () => {
                                                                            await handleViewBatch(batch.id);
                                                                        }}
                                                                    >
                                                                        <IconEye size={16} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            </Group>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                ))}
                                            </Table.Tbody>
                                        </Table>
                                    </>
                                )}
                            {selectedBatch !== null && (
                                <Stack>
                                    <Group justify="space-between" align="center">
                                        <h3>{`Batch ${selectedBatch.batchId} Summary`}</h3>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                resetRecordsViewStates();
                                            }}
                                        >
                                            Back to Batches
                                        </Button>
                                    </Group>
                                    <Stack gap={4}>
                                        <Group justify="space-between" align="center">
                                            <Text>{`Batch Date: ${formatDate(
                                                selectedBatch.createdAt
                                            )}`}</Text>
                                        </Group>
                                        <Text>{`Total Records: ${selectedBatch.totalRecords}`}</Text>
                                        <Text>{`Letters Required: ${selectedBatch.totalRecordsMatchingDocumentTypes}`}</Text>
                                        <Text>{`Letters Generated: ${selectedBatch.grantorGranteeIndexRecords?.length ?? 0}`}</Text>
                                        <Text>{`Letters Failed: ${selectedBatch.grantorGranteeIndexFailedRecords?.length ?? 0}`}</Text>
                                        <Text>{`Last Printed: ${selectedBatch.lastPrinted
                                            ? formatMailerDate(selectedBatch.lastPrinted)
                                            : `${formatDate(selectedBatch.createdAt)}`
                                            }`}</Text>
                                        {/* <Group>
                                            <Tooltip label="Regenerate Letters, this may take a few minutes and will be delivered via method selected" withArrow>
                                                <Button loading={selectedBatch.regenerateMailersInProgress || selectedBatch.jobId !== undefined} mt={10} size={"md"} onClick={handleRegenerateMailersForBatch}>Reprint Letters</Button>
                                            </Tooltip>
                                        </Group> */}
                                        <Group>
                                        <Tooltip label="View Batch Document" withArrow>
                                            <Button loading={batchDownloadInProgress} mt={10} size={"md"} onClick={async () => {
                                                await viewBatchDocument(selectedBatch);
                                            }}>View Batch Document</Button>
                                        </Tooltip>
                                        </Group>

                                    </Stack>
                                    <Divider my="sm" />

                                    <h3>{`Letters Generated`}</h3>
                                    {selectedBatch.grantorGranteeIndexRecords.length === 0 && (
                                        <Alert color="yellow">
                                            No Letters Generated found for this batch.
                                        </Alert>
                                    )}
                                    {((visibleProcessedRecords?.length ?? 0) > 0 ||
                                        selectedBatch.grantorGranteeIndexRecords.length > 0) && (
                                            <Box pos="relative">
                                                <LoadingOverlay visible={batchRecordUpdateInProgress} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
                                                <Group justify="flex-end" mt="md">
                                                    <Button onClick={async () => {
                                                        await handleMarkBatchAsMailed(selectedBatch.id);
                                                    }}>
                                                        Mark All as Mailed
                                                    </Button>
                                                    <TextInput
                                                        placeholder="Search..."
                                                        value={searchProcessedRecordsText}
                                                        onChange={(event) =>
                                                            setSearchProcessedRecordsText(
                                                                event.currentTarget.value
                                                            )
                                                        }
                                                    />
                                                    <Select
                                                        value={processedRecordsPageSize.toString()}
                                                        onChange={(value) =>
                                                            setProcessedRecordsPageSize(Number(value))
                                                        }
                                                        data={[
                                                            { value: "10", label: "10" },
                                                            { value: "20", label: "20" },
                                                            { value: "50", label: "50" },
                                                            { value: "100", label: "100" },
                                                        ]}
                                                    />
                                                    <Pagination
                                                        total={Math.ceil(
                                                            (filteredProcessedRecords?.length ?? 0) /
                                                            processedRecordsPageSize
                                                        )}
                                                        value={processedRecordsPage + 1}
                                                        onChange={(value) => {
                                                            setProcessedRecordsPage(value - 1);
                                                        }}
                                                    />
                                                </Group>
                                                <Table striped highlightOnHover>
                                                    <Table.Thead>
                                                        <Table.Tr>
                                                            {/* <Table.Th>
                                            <Group tabIndex={0} role="button" onClick={() => {
                                                const newSortTerm = processedRecordsSortTerm.includes('id') ? (processedRecordsSortTerm.includes('asc') ? 'id desc' : 'id asc') : 'id asc';
                                                setProcessedRecordsSortTerm(newSortTerm);
                                            }}><Text>Record ID</Text>
                                            {processedRecordsSortTerm.includes('id') ? processedRecordsSortTerm.includes('desc') ? <IconSortDescendingLetters /> : <IconSortAscendingLetters /> : <IconArrowsSort />}
                                            </Group>
                                        </Table.Th> */}
                                                            <Table.Th>
                                                                <Group tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            processedRecordsSortTerm.includes("apn")
                                                                                ? processedRecordsSortTerm.includes("asc")
                                                                                    ? "apn desc"
                                                                                    : "apn asc"
                                                                                : "apn asc";
                                                                        setProcessedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>APN</Text>
                                                                    {processedRecordsSortTerm.includes("apn") ? (
                                                                        processedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Group tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            processedRecordsSortTerm.includes(
                                                                                "documentDate"
                                                                            )
                                                                                ? processedRecordsSortTerm.includes("asc")
                                                                                    ? "documentDate desc"
                                                                                    : "documentDate asc"
                                                                                : "documentDate asc";
                                                                        setProcessedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>Document Date</Text>
                                                                    {processedRecordsSortTerm.includes(
                                                                        "documentDate"
                                                                    ) ? (
                                                                        processedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Group tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            processedRecordsSortTerm.includes(
                                                                                "documentNumber"
                                                                            )
                                                                                ? processedRecordsSortTerm.includes("asc")
                                                                                    ? "documentNumber desc"
                                                                                    : "documentNumber asc"
                                                                                : "documentNumber asc";
                                                                        setProcessedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>Document Number</Text>
                                                                    {processedRecordsSortTerm.includes(
                                                                        "documentNumber"
                                                                    ) ? (
                                                                        processedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Document Type</Text>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Letter Created</Text>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Mailed</Text>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Mailed On</Text>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Actions</Text>
                                                            </Table.Th>
                                                        </Table.Tr>
                                                    </Table.Thead>
                                                    <Table.Tbody style={{fontSize: 13}}>
                                                        {visibleProcessedRecords?.map((record) => (
                                                            <Table.Tr key={record.id}>
                                                                {/* <Table.Td>{record.id}</Table.Td> */}
                                                                <Table.Td>{record.apn}</Table.Td>
                                                                <Table.Td>{record.documentDate}</Table.Td>
                                                                <Table.Td>{record.documentNumber}</Table.Td>
                                                                <Table.Td>{record.documentTitle}</Table.Td>
                                                                <Table.Td>
                                                                    {formatMailerDate(record.createdAt)}
                                                                </Table.Td>
                                                                <Table.Td>
                                                                    <Checkbox checked={record.isMailed} onChange={async () => {
                                                                        await handleToggleRecordMailedStatus(record.id, selectedBatch.id);
                                                                    }} />
                                                                </Table.Td>
                                                                <Table.Td>
                                                                    <Group>
                                                                        <DateInput 
                                                                            value={record.mailedAt ? dayjs(record.mailedAt).toDate() : null}
                                                                            onChange={(e: DateValue) => {
                                                                                setSelectedBatch((prevBatch) => {
                                                                                    if (!prevBatch) return prevBatch;
                                                                                    return {
                                                                                        ...prevBatch,
                                                                                        grantorGranteeIndexRecords: prevBatch.grantorGranteeIndexRecords.map((r) => r.id === record.id ? { ...r, mailedAt: e ? dayjs(e).format('YYYY-MM-DD') : undefined } : r)
                                                                                    };
                                                                                });
                                                                            }}
                                                                            placeholder="Select date" 
                                                                            rightSection= {
                                                                                    <Tooltip
                                                                                        label={`Set mailed date for ${record.documentNumber}`}
                                                                                        withArrow
                                                                                    >
                                                                                    <ActionIcon
                                                                                        variant="subtle"
                                                                                        onClick={async () => {
                                                                                            if(!record.mailedAt) {
                                                                                                return;
                                                                                            }
                                                                                        await handleUpdateRecordMailedDate(record.id, selectedBatch.id, record.mailedAt);
                                                                                        }}
                                                                                        >   
                                                                                        <IconCheck size={16} />
                                                                                    </ActionIcon></Tooltip>
                                                                            }                                                                            
                                                                        />
                                                                        {record.mailedAt ? <Tooltip 
                                                                                    label={`Clear mailed date for ${record.documentNumber}`}
                                                                                    withArrow
                                                                                    ><ActionIcon
                                                                                    variant="outline"
                                                                                    
                                                                                    onClick={async () => {
                                                                                    await handleClearRecordMailedDate(record.id, selectedBatch.id);
                                                                                    }}>
                                                                                        <IconX size={16} />
                                                                                </ActionIcon></Tooltip> : null}
                                                                    </Group>
                                                                    
                                                                </Table.Td>
                                                                <Table.Td>
                                                                    <Group>
                                                                        {/* <Tooltip
                                                                            label={`Reprint letter for document ${record.documentNumber}`}
                                                                            withArrow
                                                                        >
                                                                            <ActionIcon
                                                                                variant="outline"
                                                                                color="pq-red"
                                                                                aria-label={`Reprint letter for document ${record.documentNumber}`}
                                                                                loading={record.jobId !== undefined || record.generatingInProcess === true}
                                                                                onClick={() => {
                                                                                    handleRecreateMailer(record);
                                                                                }}
                                                                            >
                                                                                <IconReload size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip> */}
                                                                        <Tooltip
                                                                            label={`View document ${record.documentNumber}`}
                                                                            withArrow
                                                                        >
                                                                            <ActionIcon
                                                                                variant="outline"
                                                                                color="pq-red"
                                                                                aria-label={`View document ${record.documentNumber}`}
                                                                                onClick={ async () => {
                                                                                    await viewCountyDocument(record);
                                                                                }}
                                                                            >
                                                                                <IconEye size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    </Group>
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        ))}
                                                    </Table.Tbody>
                                                </Table>
                                            </Box>
                                        )}
                                    <h3>{`Letters Failed`}</h3>
                                    {selectedBatch.grantorGranteeIndexFailedRecords.length ===
                                        0 && (
                                            <Alert color="green">
                                                No Letters Failed found for this batch.
                                            </Alert>
                                        )}
                                    {((visibleFailedRecords?.length ?? 0) > 0 ||
                                        selectedBatch.grantorGranteeIndexFailedRecords.length >
                                        0) && (
                                            <Box pos="relative">
                                                <LoadingOverlay visible={batchFailedRecordUpdateInProgress} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
                                                <Group justify="flex-end" mt="md">
                                                    <Button onClick={async () => {
                                                        await handleMarkFailedRecordsBatchAsMailed(selectedBatch.id);
                                                    }}>
                                                        Mark All as Mailed
                                                    </Button>
                                                    <TextInput
                                                        placeholder="Search..."
                                                        value={searchFailedRecordsText}
                                                        onChange={(event) =>
                                                            setSearchFailedRecordsText(
                                                                event.currentTarget.value
                                                            )
                                                        }
                                                    />
                                                    <Select
                                                        value={failedRecordsPageSize.toString()}
                                                        onChange={(value) =>
                                                            setFailedRecordsPageSize(Number(value))
                                                        }
                                                        data={[
                                                            { value: "10", label: "10" },
                                                            { value: "20", label: "20" },
                                                            { value: "50", label: "50" },
                                                            { value: "100", label: "100" },
                                                        ]}
                                                    />
                                                    <Pagination
                                                        total={Math.ceil(
                                                            (filteredFailedRecords?.length ?? 0) /
                                                            failedRecordsPageSize
                                                        )}
                                                        value={failedRecordsPage + 1}
                                                        onChange={(value) => {
                                                            setFailedRecordsPage(value - 1);
                                                        }}
                                                    />
                                                </Group>
                                                <Table striped highlightOnHover horizontalSpacing="5" verticalSpacing="xs">
                                                    <Table.Thead>
                                                        <Table.Tr>
                                                            {/* <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                                const newSortTerm = failedRecordsSortTerm.includes('id') ? (failedRecordsSortTerm.includes('asc') ? 'id desc' : 'id asc') : 'id asc';
                                                setFailedRecordsSortTerm(newSortTerm);
                                            }}><Text>Record ID</Text>
                                            {failedRecordsSortTerm.includes('id') ? failedRecordsSortTerm.includes('desc') ? <IconSortDescendingLetters /> : <IconSortAscendingLetters /> : <IconArrowsSort />}
                                            </Group>
                                        </Table.Th> */}
                                                            <Table.Th>
                                                                <Group
                                                                    tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            failedRecordsSortTerm.includes("apn")
                                                                                ? failedRecordsSortTerm.includes("asc")
                                                                                    ? "apn desc"
                                                                                    : "apn asc"
                                                                                : "apn asc";
                                                                        setFailedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>APN</Text>
                                                                    {failedRecordsSortTerm.includes("apn") ? (
                                                                        failedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Group
                                                                    tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            failedRecordsSortTerm.includes(
                                                                                "documentDate"
                                                                            )
                                                                                ? failedRecordsSortTerm.includes("asc")
                                                                                    ? "documentDate desc"
                                                                                    : "documentDate asc"
                                                                                : "documentDate asc";
                                                                        setFailedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>Document Date</Text>
                                                                    {failedRecordsSortTerm.includes(
                                                                        "documentDate"
                                                                    ) ? (
                                                                        failedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Group
                                                                    tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            failedRecordsSortTerm.includes(
                                                                                "documentNumber"
                                                                            )
                                                                                ? failedRecordsSortTerm.includes("asc")
                                                                                    ? "documentNumber desc"
                                                                                    : "documentNumber asc"
                                                                                : "documentNumber asc";
                                                                        setFailedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>Document Number</Text>
                                                                    {failedRecordsSortTerm.includes(
                                                                        "documentNumber"
                                                                    ) ? (
                                                                        failedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Group
                                                                    tabIndex={0} role="button"
                                                                    onClick={() => {
                                                                        const newSortTerm =
                                                                            failedRecordsSortTerm.includes(
                                                                                "documentTitle"
                                                                            )
                                                                                ? failedRecordsSortTerm.includes("asc")
                                                                                    ? "documentTitle desc"
                                                                                    : "documentTitle asc"
                                                                                : "documentTitle asc";
                                                                        setFailedRecordsSortTerm(newSortTerm);
                                                                    }}
                                                                >
                                                                    <Text fz={13}>Document Type</Text>
                                                                    {failedRecordsSortTerm.includes(
                                                                        "documentTitle"
                                                                    ) ? (
                                                                        failedRecordsSortTerm.includes("desc") ? (
                                                                            <IconSortDescendingLetters aria-hidden="true" />
                                                                        ) : (
                                                                            <IconSortAscendingLetters aria-hidden="true" />
                                                                        )
                                                                    ) : (
                                                                        <IconArrowsSort aria-hidden="true" />
                                                                    )}
                                                                </Group>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Failure Reason</Text>
                                                            </Table.Th>
                                                            {/* <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                                const newSortTerm = failedRecordsSortTerm.includes('recreatedStatus') ? (failedRecordsSortTerm.includes('asc') ? 'recreatedStatus desc' : 'recreatedStatus asc') : 'recreatedStatus asc';
                                                setFailedRecordsSortTerm(newSortTerm);
                                            }}><Text>Recreated Status</Text>
                                            {failedRecordsSortTerm.includes('recreatedStatus') ? failedRecordsSortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}
                                            </Group>
                                        </Table.Th> */}
                                                            <Table.Th>
                                                                <Text fz={13}>Mailed</Text>
                                                            </Table.Th>    
                                                            <Table.Th>
                                                                <Text fz={13}>Mailed On</Text>
                                                            </Table.Th>
                                                            <Table.Th>
                                                                <Text fz={13}>Actions</Text>
                                                            </Table.Th>
                                                        </Table.Tr>
                                                    </Table.Thead>
                                                    <Table.Tbody style={{fontSize: 13}}>
                                                        {visibleFailedRecords?.map((record) => (
                                                            <Table.Tr key={record.id}>
                                                                {/* <Table.Td>{record.id}</Table.Td> */}
                                                                <Table.Td>{record.apn ?? "N/A"}</Table.Td>
                                                                <Table.Td>{record.documentDate}</Table.Td>
                                                                <Table.Td>{record.documentNumber}</Table.Td>
                                                                <Table.Td>{record.documentTitle}</Table.Td>
                                                                <Table.Td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {record.failureReason.includes('is null') ? 'Assessor data was not found for APN' : record.failureReason}
                                                                </Table.Td>
                                                                {/* <Table.Td>{record.recreated ? 'Mailer Recreated' : 'Not Recreated'}</Table.Td> */}
                                                                <Table.Td>
                                                                    <Checkbox checked={record.isMailed} onChange={async () => {
                                                                        await handleToggleFailedRecordMailedStatus(record.id, selectedBatch.id);
                                                                    }} />
                                                                </Table.Td>
                                                                <Table.Td>
                                                                        <Group>
                                                                            <DateInput 
                                                                                value={record.mailedAt ? dayjs(record.mailedAt).toDate() : null}
                                                                                size="xs"
                                                                                onChange={(e: DateValue) => {
                                                                                    setSelectedBatch((prevBatch) => {
                                                                                        if (!prevBatch) return prevBatch;
                                                                                        return {
                                                                                            ...prevBatch,
                                                                                            grantorGranteeIndexFailedRecords: prevBatch.grantorGranteeIndexFailedRecords.map((r) => r.id === record.id ? { ...r, mailedAt: e ? dayjs(e).format('YYYY-MM-DD') : undefined } : r)
                                                                                        };
                                                                                    });
                                                                                }}
                                                                                placeholder="Select date" 
                                                                            
                                                                                rightSection= {
                                                                                        <Tooltip
                                                                                            label={`Set mailed date for ${record.documentNumber}`}
                                                                                            withArrow
                                                                                        >
                                                                                        <ActionIcon
                                                                                            variant="subtle"
                                                                                            onClick={async () => {
                                                                                                if(!record.mailedAt) {
                                                                                                    return;
                                                                                                }
                                                                                            await handleUpdateFailedRecordMailedDate(record.id, selectedBatch.id, record.mailedAt);
                                                                                            }}
                                                                                            >   
                                                                                            <IconCheck size={16} />
                                                                                        </ActionIcon></Tooltip>
                                                                                }
                                                                                
                                                                            />
                                                                            {record.mailedAt ? <Tooltip 
                                                                                        label={`Clear mailed date for ${record.documentNumber}`}
                                                                                        withArrow
                                                                                        >
                                                                                        <ActionIcon
                                                                                        variant="outline"
                                                                                        color="pq-red"
                                                                                        onClick={async () => {
                                                                                        await handleClearFailedRecordMailedDate(record.id, selectedBatch.id);
                                                                                        }}>
                                                                                            <IconX size={16} />
                                                                                    </ActionIcon></Tooltip> : null}
                                                                        </Group>
                                                                </Table.Td>
                                                                <Table.Td>
                                                                    <Group>
                                                                        <Tooltip
                                                                            label={`Edit/Generate new letter for ${record.documentNumber}`}
                                                                            withArrow
                                                                        >
                                                                            <ActionIcon
                                                                                variant="outline"
                                                                                color="pq-red"
                                                                                aria-label={`Edit/Generate new letter for ${record.documentNumber}`}
                                                                                loading={record.jobId !== undefined || record.generatingInProcess === true}
                                                                                onClick={() => {
                                                                                    handleGenerateFailedRecordMailer(
                                                                                        record
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <IconReload size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                         <Tooltip
                                                                            label={`View document ${record.documentNumber}`}
                                                                            withArrow
                                                                        >
                                                                            <ActionIcon
                                                                                variant="outline"
                                                                                color="pq-red"
                                                                                aria-label={`View document ${record.documentNumber}`}
                                                                                onClick={ async () => {
                                                                                    await viewCountyDocument(record);
                                                                                }}
                                                                            >
                                                                                <IconEye size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    </Group>
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        ))}
                                                    </Table.Tbody>
                                                </Table>
                                            </Box>
                                        )}
                                </Stack>
                            )}
                        </Paper>
            </Stack>
            <Modal
                opened={generateFailedRecordsModalOpen}
                onClose={generateFailedRecordsModalHandles.close}
                size="lg"
                title="Edit/Generate Letter"
            >
                <Stack>
                    <TextInput
                        label="Document Number"
                        value={selectedFailedRecord?.documentNumber}
                        disabled
                        readOnly
                    />
                    <TextInput
                        label="Names"
                        value={selectedFailedRecord?.names}
                        onChange={(e) => {
                            if (selectedFailedRecord) {
                                setSelectedFailedRecord({
                                    ...selectedFailedRecord,
                                    names: e.currentTarget.value,
                                });
                            }
                        }}
                    />
                    <TextInput
                        label="Situs Address or Legal Description"
                        value={selectedFailedRecord?.situsAddressOrLegalDescription}
                        onChange={(e) => {
                            if (selectedFailedRecord) {
                                setSelectedFailedRecord({
                                    ...selectedFailedRecord,
                                    situsAddressOrLegalDescription: e.currentTarget.value,
                                });
                            }
                        }}
                    />
                    <Combobox
                        store={apnSearchCombobox}
                        withinPortal={false}
                        onOptionSubmit={(value) => handleApnSelected(value)}
                    >
                        <Combobox.Target>
                            <InputBase
                                label="APN"
                                value={selectedFailedRecord?.apn ?? ""}
                                onChange={(e) => {
                                    const value = countyDetails?.uiMask
                                        ? maskValue(e.currentTarget.value, countyDetails.uiMask)
                                        : e.currentTarget.value;
                                    if (selectedFailedRecord) {
                                        setSelectedFailedRecord({
                                            ...selectedFailedRecord,
                                            apn: value,
                                        });
                                    }
                                    setApnSearch(value);
                                    apnSearchCombobox.openDropdown();
                                }}
                                onFocus={() => {
                                    const currentApn = selectedFailedRecord?.apn ?? "";
                                    if (currentApn !== lastSearchedApn.current) {
                                        searchApns(currentApn);
                                    }
                                    apnSearchCombobox.openDropdown();
                                }}
                                onBlur={() => apnSearchCombobox.closeDropdown()}
                            />
                        </Combobox.Target>
                        <Combobox.Dropdown>
                            <Combobox.Options>
                                <ScrollArea.Autosize type="scroll" mah={200}>
                                    {apnSearchResults.length === 0 ? (
                                        <Combobox.Empty>No matching APNs</Combobox.Empty>
                                    ) : (
                                        apnSearchResults.slice(0, 10).map((result, i) => (
                                            <Combobox.Option key={i} value={result.apn}>
                                                {result.apn}
                                            </Combobox.Option>
                                        ))
                                    )}
                                </ScrollArea.Autosize>
                            </Combobox.Options>
                        </Combobox.Dropdown>
                    </Combobox>
                    <TextInput
                        label="Recording Date"
                        value={selectedFailedRecord?.recordingDate}
                        disabled
                        readOnly
                    />
                    <TextInput
                        label="Generated Date"
                        value={selectedFailedRecord?.generatedDate}
                        disabled
                        readOnly
                    />
                    <TextInput
                        label="Mailing Address"
                        value={selectedFailedRecord?.mailingAddress}
                        onChange={(e) => {
                            if (selectedFailedRecord) {
                                setSelectedFailedRecord({
                                    ...selectedFailedRecord,
                                    mailingAddress: e.currentTarget.value,
                                });
                            }
                        }}
                    />
                    <TextInput
                        label="Mailing City"
                        value={selectedFailedRecord?.mailingCity}
                        onChange={(e) => {
                            if (selectedFailedRecord) {
                                setSelectedFailedRecord({
                                    ...selectedFailedRecord,
                                    mailingCity: e.currentTarget.value,
                                });
                            }
                        }}
                    />
                    <TextInput
                        label="Mailing State"
                        value={selectedFailedRecord?.mailingState}
                        onChange={(e) => {
                            if (selectedFailedRecord) {
                                setSelectedFailedRecord({
                                    ...selectedFailedRecord,
                                    mailingState: e.currentTarget.value,
                                });
                            }
                        }}
                    />
                    <TextInput
                        label="Mailing Zip"
                        value={selectedFailedRecord?.mailingZip}
                        onChange={(e) => {
                            if (selectedFailedRecord) {
                                setSelectedFailedRecord({
                                    ...selectedFailedRecord,
                                    mailingZip: e.currentTarget.value,
                                });
                            }
                        }}
                    />
                    <Group justify={"flex-end"}>
                        <Button
                            variant="outline"
                            onClick={generateFailedRecordsModalHandles.close}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="pq-red"
                            onClick={handleGenerateFailedRecordMailerSubmit}
                        >
                            Generate Letter
                        </Button>
                    </Group>
                    {failedRecordError && <Alert color="red">{failedRecordError}</Alert>}
                </Stack>
            </Modal>
            {/* <Modal opened={deliveryMethodModalOpen}
                onClose={deliveryMethodModalHandles.close}
                size="lg"
                title="Edit Delivery Method"
            >
                <Tabs defaultValue={deliveryMethod === 'FTP' ? 'FTP' : 'Email'}>
                    <Tabs.List>
                        <Tabs.Tab value="Email">Email</Tabs.Tab>
                        <Tabs.Tab value="FTP">FTP</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="Email" pt="xs">
                        <Stack>
                            <TextInput label="Email Address" value={emailAddress} onChange={(e) => {
                                setEmailAddress(e.currentTarget.value);
                            }} />
                            <Text>By selecting Email as your delivery method, all county mailers will be sent to your registered email address.</Text>
                            <Button
                                color="pq-red"
                                onClick={() => {
                                    handleSetCountyDeliveryMethod('Email');
                                }}
                            >
                                Set Delivery Method to Email
                            </Button>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="FTP" pt="xs">
                        <Stack>
                            <Select label="FTP Protocol" value={ftpProtocol} onChange={(value) => {
                                setFtpProtocol(value as 'ftp' | 'sftp');
                            }} data={[
                                { value: 'ftp', label: 'FTP' },
                                { value: 'sftp', label: 'SFTP' },
                            ]} />
                            <TextInput label="Host" value={ftpHost} onChange={(e) => {
                                setFtpHost(e.currentTarget.value);
                            }} />
                            <NumberInput label="Port" value={ftpPort} onChange={setFtpPort} min={1} max={65535} step={1} />
                            <TextInput label="Username" value={ftpUsername} onChange={(e) => {
                                setFtpUsername(e.currentTarget.value);
                            }} />
                            <PasswordInput label="Password" type="password" value={ftpPassword} onChange={(e) => {
                                setFtpPassword(e.currentTarget.value);
                            }} />
                            <TextInput label="Directory" value={ftpDirectory} onChange={(e) => {
                                setFtpDirectory(e.currentTarget.value);
                            }} />
                            <Checkbox label="Use Passive Mode" checked={ftpPassiveMode} onChange={(e) => {
                                setFtpPassiveMode(e.currentTarget.checked);
                            }} />
                            <Checkbox label="Use SSL/TLS" checked={ftpUseSsl} onChange={(e) => {
                                setFtpUseSsl(e.currentTarget.checked);
                            }} />
                            <Text>By selecting FTP as your delivery method, all county mailers will be uploaded to your specified FTP server.</Text>
                            <Button
                                color="pq-red"
                                onClick={() => {
                                    handleSetCountyDeliveryMethod('FTP');
                                }}
                            >
                                Set Delivery Method to FTP
                            </Button>
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Modal> */}
        </>
    );
};

export default CountyReports;
